import React, { useState, useEffect } from "react";
import Axios from "../config/AxiosConfig";
import { XCircle, ToggleOn, ToggleOff } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RestaurantModal from "./models/RestaurantModal";
import ImageModal from "./models/ImageModal";

const RestaurantTracker = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [imageList, setImageList] = useState([]);
  const [imageTitle, setImageTitle] = useState("");

  useEffect(() => {
    fetchRestaurants();
  }, [page, searchQuery, sortOption]);

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(`super-admin/restaurants`, {
        params: { search: searchQuery, page, limit: 10, sort: sortOption },
      });
      setRestaurants(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setRestaurants([]);
    }
    setLoading(false);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchQuery("");
  };

  const openModal = async (id) => {
    try {
      const response = await Axios.get(`super-admin/restaurant/${id}`);
      setSelectedRestaurant(response.data.data);
    } catch (error) {
      toast.error("Error fetching restaurant details.");
    }
  };

  const openImageModal = (images, title) => {
    setImageList(images);
    setImageTitle(title);
  };

  const toggleRestaurantStatus = async (restaurantId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await Axios.put(`/super-admin/restaurant-status/${restaurantId}`, { isEnabled: newStatus });
      toast.success(`Restaurant ${newStatus ? "enabled" : "disabled"} successfully`);
      fetchRestaurants();
    } catch (error) {
      toast.error("Failed to update restaurant status");
      console.error("Error toggling restaurant status:", error);
    }
  };

  return (
    <div className="restaurant-tracker-container">
      <h2>Restaurants List</h2>
      <div className="header-search-container">
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search by Restaurant Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <XCircle className="clear-icon" size={18} onClick={handleClearSearch} />
            )}
          </div>
          <button onClick={() => setSearchQuery(searchTerm)}>Search</button>
        </div>

        <select className="sort-dropdown" onChange={(e) => setSortOption(e.target.value)}>
          <option value="">Sort By</option>
          <option value="premium">Premium</option>
          <option value="non-premium">Non-Premium</option>
        </select>
      </div>

      <table className="restaurant-table">
        <thead>
          <tr>
            <th>SNo</th>
            <th>Restaurant Name</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Premium</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className="loading-cell"><p>Loading...</p></td>
            </tr>
          ) : restaurants.length > 0 ? (
            restaurants.map((restaurant, index) => (
              <tr key={restaurant._id}>
                <td>{index + 1 + (page - 1) * 10}</td>
                <td>{restaurant.restaurantName}</td>
                <td>{restaurant.email}</td>
                <td>{restaurant.mobileNumber}</td>
                <td>{restaurant.premiumAccess.isPremium ? "Yes" : "No"}</td>
                <td>
                  {restaurant.isEnabled ? (
                    <ToggleOn className="status-enabled" size={26} onClick={() => toggleRestaurantStatus(restaurant._id, restaurant.isEnabled)} />
                  ) : (
                    <ToggleOff className="status-disabled" size={26} onClick={() => toggleRestaurantStatus(restaurant._id, restaurant.isEnabled)} />
                  )}
                </td>
                <td>
                  <button className="view-details-btn" onClick={() => openModal(restaurant._id)}>
                    View Details
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">No restaurants found</td>
            </tr>
          )}
        </tbody>
      </table>
      
      {restaurants.length > 0 && (
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} className={page === i + 1 ? "active" : ""} onClick={() => setPage(i + 1)}>
              {i + 1}
            </button>
          ))}
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}

      {selectedRestaurant && (
        <RestaurantModal
          isOpen={Boolean(selectedRestaurant)}
          restaurant={selectedRestaurant}
          closeModal={() => setSelectedRestaurant(null)}
          openImageModal={openImageModal}
        />
      )}

      {imageList.length > 0 &&
        !(Array.isArray(imageList) && imageList.length === 1 && imageList[0] === "") && (
          <ImageModal images={imageList} title={imageTitle} closeModal={() => setImageList([])} />
        )}
    </div>
  );
};

export default RestaurantTracker;
